
import React, { useRef, useState } from 'react';
import { FileUp, Loader2, Info } from 'lucide-react';
import * as XLSX from 'xlsx';
import Swal from 'sweetalert2';
import { createCategory, addAthleteToCategory } from '../services/api';

export default function FileImporter({ competitionId, existingCategories, onImportSuccess, readOnly = false }) {
  const fileInputRef = useRef(null);
  const [importing, setImporting] = useState(false);

  const normalizeText = (text) => {
    if (!text) return "";
    return String(text)
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "") // Supprime les accents
        .toLowerCase()
        .replace(/[^a-z0-9]/g, "") // Supprime TOUT sauf lettres et chiffres (même les espaces)
        .trim();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setImporting(true);
    const reader = new FileReader();

    reader.onload = async (evt) => {
      try {
        const data = new Uint8Array(evt.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        
        let jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: "" });

        if (jsonData.length > 0 && Object.keys(jsonData[0]).length === 1) {
            const rawText = new TextDecoder('utf-8').decode(data);
            const repairWorkbook = XLSX.read(rawText, { type: 'string', FS: ';' });
            jsonData = XLSX.utils.sheet_to_json(repairWorkbook.Sheets[repairWorkbook.SheetNames[0]], { defval: "" });
        }

        if (jsonData.length === 0) throw new Error("Fichier vide");
        
        await processImport(jsonData);
      } catch (error) {
        console.error("Erreur import:", error);
        Swal.fire('Erreur', "Impossible de lire le fichier. Assurez-vous qu'il s'agit d'un fichier CSV ou Excel valide.", 'error');
      } finally {
        setImporting(false);
        if (fileInputRef.current) fileInputRef.current.value = "";
      }
    };

    reader.readAsArrayBuffer(file);
  };

  const processImport = async (data) => {
    const firstRow = data[0];
    const fileColumns = Object.keys(firstRow);
    
    // Détection des colonnes (insensible à la casse et flexible)
    let nomKey = fileColumns.find(k => normalizeText(k) === "nom");
    let prenomKey = fileColumns.find(k => normalizeText(k) === "prenom");
    let poidsKey = fileColumns.find(k => normalizeText(k) === "poids");
    let clubKey = fileColumns.find(k => normalizeText(k) === "club");

    // Replis si les noms exacts ne sont pas trouvés (compatibilité avec l'ancien format si nécessaire)
    if (!nomKey) nomKey = fileColumns.find(k => normalizeText(k).includes("nom"));
    if (!prenomKey) prenomKey = fileColumns.find(k => normalizeText(k).includes("prenom"));
    if (!poidsKey) poidsKey = fileColumns.find(k => normalizeText(k).includes("poids") || normalizeText(k).includes("cat"));
    if (!clubKey) clubKey = fileColumns.find(k => normalizeText(k).includes("club") || normalizeText(k).includes("instan"));

    if (!nomKey || !prenomKey || !poidsKey) {
        const detected = fileColumns.map(k => `${k}`).join(', ');
        Swal.fire({
            title: 'Colonnes manquantes',
            html: `<div style="text-align:left; font-size:14px;">
                    Le fichier doit contenir les colonnes : <b>nom, prenom, poids, club</b>.<br><br>
                    <b>Colonnes détectées :</b><br>
                    <code style="background:#f4f4f4;padding:5px;display:block;margin-top:5px;">${detected}</code>
                   </div>`,
            icon: 'error'
        });
        return;
    }

    let importedCount = 0;
    let duplicateCount = 0;
    const categoriesToProcess = {};

    data.forEach(row => {
        const catName = String(row[poidsKey] || "Sans catégorie").trim();
        const lastName = String(row[nomKey] || "").trim();
        const firstName = String(row[prenomKey] || "").trim();
        const athleteFullName = `${lastName} ${firstName}`.trim();
        const clubName = clubKey ? String(row[clubKey] || "").trim() : "";

        if (!athleteFullName || athleteFullName === " ") return;

        if (!categoriesToProcess[catName]) categoriesToProcess[catName] = [];
        categoriesToProcess[catName].push({ 
            name: athleteFullName, 
            nom: lastName, 
            prenom: firstName, 
            club: clubName 
        });
    });

    const totalAthletes = Object.values(categoriesToProcess).reduce((sum, arr) => sum + arr.length, 0);
    let processedCount = 0;

    const updateProgress = () => {
        const pct = totalAthletes > 0 ? Math.round((processedCount / totalAthletes) * 100) : 0;
        const container = Swal.getHtmlContainer();
        if (container) {
            const bar = container.querySelector('#import-progress-bar');
            const label = container.querySelector('#import-progress-label');
            if (bar) bar.style.width = `${pct}%`;
            if (label) label.textContent = `${processedCount} / ${totalAthletes} athlètes traités (${pct}%)`;
        }
    };

    try {
        Swal.fire({
            title: 'Importation en cours...',
            html: `
                <div style="text-align:left; font-size:14px; color:#555; margin-bottom:8px;" id="import-progress-label">
                    0 / ${totalAthletes} athlètes traités (0%)
                </div>
                <div style="background:#e2e8f0; border-radius:8px; overflow:hidden; height:16px;">
                    <div id="import-progress-bar" style="height:100%; width:0%; background:linear-gradient(90deg,#38a169,#68d391); border-radius:8px; transition:width 0.2s ease;"></div>
                </div>
            `,
            allowOutsideClick: false,
            showConfirmButton: false,
        });

        const updatedCategories = [...existingCategories];

        for (const catName of Object.keys(categoriesToProcess)) {
            let category = updatedCategories.find(c => c.name.toLowerCase() === catName.toLowerCase());

            if (!category) {
                category = await createCategory({ name: catName, competition_id: competitionId });
                updatedCategories.push(category);
            }

            const existingAthletes = category.athletes || [];

            for (const athlete of categoriesToProcess[catName]) {
                const isDuplicate = existingAthletes.some(a =>
                    (a.name || `${a.nom} ${a.prenom}`).toLowerCase().trim() === athlete.name.toLowerCase().trim() &&
                    (a.club || "").toLowerCase().trim() === athlete.club.toLowerCase().trim()
                );

                if (isDuplicate) {
                    duplicateCount++;
                } else {
                    const newAthlete = await addAthleteToCategory(category.id, { name: athlete.name, club: athlete.club }, competitionId);
                    existingAthletes.push(newAthlete);
                    importedCount++;
                }
                processedCount++;
                updateProgress();
            }
        }

        await onImportSuccess();
        Swal.fire({
            icon: 'success',
            title: 'Import terminé',
            text: `${importedCount} athlètes importés. ${duplicateCount} doublons ignorés.`,
        });

    } catch (err) {
        console.error(err);
        Swal.fire('Erreur technique', "Une erreur est survenue pendant l'importation.", 'error');
    }
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        style={{ display: 'none' }}
        accept=".csv, .xlsx, .xls"
      />
      <button
        onClick={() => !readOnly && fileInputRef.current.click()}
        disabled={importing || readOnly}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          backgroundColor: '#38a169',
          color: 'white',
          border: 'none',
          padding: '10px 20px',
          borderRadius: '8px',
          cursor: (importing || readOnly) ? 'not-allowed' : 'pointer',
          fontWeight: '600',
          fontSize: '14px'
        }}
      >
        {importing ? <Loader2 size={18} className="animate-spin" /> : <FileUp size={18} />}
        {importing ? 'Importation...' : 'Importer CSV/Excel'}
      </button>

      <div 
        title="Le fichier doit contenir les colonnes : nom, prenom, poids, club (insensible à la casse)"
        style={{ 
          display: 'flex', 
          alignItems: 'center', 
          color: '#718096', 
          cursor: 'help' 
        }}
      >
        <Info size={20} />
      </div>
    </div>
  );
}
